// ============================================================================
// EXPENSE TYPE MASTER SCREEN — MSTR-EXPTYPE-001
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useExpenseTypes } from 'hooks/api/useExpenseTypes';
import { expenseTypeService } from 'api/services/expenseTypeService';
import type { ExpenseType, ExpenseTypeSearchRequest } from 'api/types/expenseType.types';
import ExpenseTypeFormModal from 'components/modals/modules/expense-type-module/ExpenseTypeFormModal';

type ViewMode = 'list' | 'grid';

interface ExpenseTypeMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const ExpenseTypeMasterScreen: React.FC<ExpenseTypeMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<ExpenseTypeSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useExpenseTypes(queryParams);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [expenseCodeFilter, setExpenseCodeFilter] = useState('');
  const [expenseNameFilter, setExpenseNameFilter] = useState('');
  const [glCodeFilter, setGlCodeFilter] = useState('');
  const [reimbursableFilter, setReimbursableFilter] = useState<string>(''); // '' | 'true' | 'false'
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selected, setSelected] = useState<ExpenseType | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(e => e.expenseCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toDelete, setToDelete] = useState<ExpenseType | null>(null);

  const handleAddNew = useCallback(() => {
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelected(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback(
    (e: ExpenseType) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${e.expenseCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelected(e);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleView = useCallback(
    (e: ExpenseType) => {
      const modalId = tabId ? `${tabId}-view-modal-${e.expenseCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelected(e);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (e: ExpenseType) => {
      setSelected(e);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((e: ExpenseType) => {
    setToDelete(e);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!toDelete) return;
    setIsDeleting(true);
    try {
      await expenseTypeService.delete(toDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Expense Type "${toDelete.expenseCode}" deleted successfully.`);
      setToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [toDelete, refetch]);

  const handleSaved = useCallback(() => { refetch(); }, [refetch]);
  const handleRefresh = useCallback(() => { refetch(); }, [refetch]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    let isReimbursable: boolean | undefined;
    if (reimbursableFilter === 'true') isReimbursable = true;
    else if (reimbursableFilter === 'false') isReimbursable = false;
    else isReimbursable = undefined;

    setQueryParams(prev => ({
      ...prev,
      expenseCode: expenseCodeFilter.trim() || undefined,
      expenseName: expenseNameFilter.trim() || undefined,
      glCode: glCodeFilter.trim() || undefined,
      isReimbursable,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [expenseCodeFilter, expenseNameFilter, glCodeFilter, reimbursableFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setExpenseCodeFilter('');
    setExpenseNameFilter('');
    setGlCodeFilter('');
    setReimbursableFilter('');
    setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.expenseCode) count++;
    if (queryParams.expenseName) count++;
    if (queryParams.glCode) count++;
    if (queryParams.isReimbursable !== undefined) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.expenseCode, queryParams.expenseName, queryParams.glCode, queryParams.isReimbursable, queryParams.statusCode]);

  const columns: SyutGridColumn<ExpenseType>[] = useMemo(() => [
    {
      id: 'expenseCode',
      header: 'Code',
      accessorKey: 'expenseCode',
      size: 140,
      enableSorting: true,
    },
    {
      id: 'expenseName',
      header: 'Name',
      accessorKey: 'expenseName',
      size: 260,
      enableSorting: true,
      flex: true,
    },
    {
      id: 'glCode',
      header: 'GL Code',
      accessorKey: 'glCode',
      size: 120,
      enableSorting: true,
      cell: ({ row }) => row.original.glCode ?? '-',
    },
    {
      id: 'isReimbursable',
      header: 'Reimbursable',
      accessorKey: 'isReimbursable',
      size: 130,
      enableSorting: true,
      cell: ({ row }) => {
        const val = row.original.isReimbursable;
        const color = val ? '#16a34a' : '#6b7280';
        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color,
              padding: '2px 8px',
              border: `1px solid ${color}`,
              borderRadius: '999px',
            }}
          >
            {val ? 'Yes' : 'No'}
          </span>
        );
      },
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        const e = row.original;
        const code = e.deletedAt ? 'DELETED' : (e.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color,
              padding: '2px 8px',
              border: `1px solid ${color}`,
              borderRadius: '999px',
            }}
            title={e.deletedAt ? `Deleted at ${new Date(e.deletedAt).toLocaleString()}` : undefined}
          >
            {code}
          </span>
        );
      },
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      accessorKey: 'updatedAt',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.updatedAt;
        if (!v) return '-';
        try {
          return new Date(v).toLocaleString();
        } catch {
          return v;
        }
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  const getRowActions = useCallback((e: ExpenseType): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(e) },
    { id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={16} />,
      onClick: () => handleEdit(e),
    },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(e) },
    { id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={16} />,
      onClick: () => handleDeleteClick(e),
      variant: 'danger',
    },
  ], [handleEdit, handleView, handleCopy, handleDeleteClick]);

  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add',
      tooltip: 'Add New Expense Type',
      onClick: handleAddNew,
      variant: 'icon',
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      onClick: handleRefresh,
      variant: 'icon',
    },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Expense Type Master"
        description="Case-level expense categories (Court Fee, Advocate Fee, Stamp Duty, Travel, etc.). Carries GL mapping and reimbursable flag."
        icon={<IconSystem.dollarSign size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, GL...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No expense types found"
              />
            )}

            {viewMode === 'grid' && (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: '1rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.map((e) => (
                    <div
                      key={String(e.id)}
                      style={{
                        position: 'relative',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleEdit(e)}
                    >
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(e)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {e.expenseCode} · {e.glCode ?? '—'}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {e.expenseName}
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: e.isReimbursable ? '#16a34a' : '#6b7280' }}>
                        {e.isReimbursable ? 'Reimbursable' : 'Non-reimbursable'}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Expense Code</label>
              <input
                type="text"
                className="form-input"
                value={expenseCodeFilter}
                onChange={(e) => setExpenseCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Expense Name</label>
              <input
                type="text"
                className="form-input"
                value={expenseNameFilter}
                onChange={(e) => setExpenseNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>GL Code</label>
              <input
                type="text"
                className="form-input"
                value={glCodeFilter}
                onChange={(e) => setGlCodeFilter(e.target.value)}
                placeholder="Filter by GL code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Reimbursable"
                options={[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Yes (reimbursable)' },
                  { value: 'false', label: 'No (non-reimbursable)' },
                ]}
                value={reimbursableFilter}
                onChange={(v) => setReimbursableFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={
          toDelete
            ? `Are you sure you want to delete expense type "${toDelete.expenseCode}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setToDelete(null);
          }
        }}
      />

      <ExpenseTypeFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selected}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default ExpenseTypeMasterScreen;
